import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Warehouse, Pill, Shield, BarChart3, Package, ClipboardList,
  Users, Truck, Heart, Activity, Check, X, CreditCard, TrendingUp,
  ArrowRight
} from 'lucide-react';
import { COLORS } from './landingData';

export function EcosystemSection({ lang, activeEco, setActiveEco }) {
  const navigate = useNavigate();

  return (
    <section id="features" style={{ background: '#0e1e2f', padding: '72px 24px 0', overflow: 'hidden' }} data-testid="features-section">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: 800, color: '#fff', lineHeight: 1.1, margin: '0 0 12px' }}>
            UNIFIED SUCCESS
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', maxWidth: 580, margin: '0 auto', lineHeight: 1.6 }}>
            {lang === 'en'
              ? <>A connected ecosystem that drives <span style={{ color: COLORS.accent, fontWeight: 700 }}>efficiency</span>, eliminates <span style={{ color: '#F59E0B', fontWeight: 700 }}>stockouts</span>, and maximizes <span style={{ color: COLORS.secondary, fontWeight: 700 }}>revenue</span>.</>
              : lang === 'ru'
              ? <>Экосистема, которая повышает <span style={{ color: COLORS.accent, fontWeight: 700 }}>эффективность</span>, устраняет <span style={{ color: '#F59E0B', fontWeight: 700 }}>дефицит</span> и увеличивает <span style={{ color: COLORS.secondary, fontWeight: 700 }}>доход</span>.</>
              : <>Экосистемае, ки <span style={{ color: COLORS.accent, fontWeight: 700 }}>самаранокӣ</span>-ро баланд мебарад, <span style={{ color: '#F59E0B', fontWeight: 700 }}>камбудро</span> бартараф мекунад ва <span style={{ color: COLORS.secondary, fontWeight: 700 }}>даромадро</span> зиёд мекунад.</>}
          </p>
        </div>

        {/* Toggle Tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: 40 }}>
          <button
            onClick={() => setActiveEco('dist')}
            data-testid="eco-tab-dist"
            style={{
              padding: '12px 32px', fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 14,
              border: 'none', cursor: 'pointer', transition: 'all 0.35s ease',
              borderRadius: '50px 0 0 50px',
              background: activeEco === 'dist' ? COLORS.secondary : 'rgba(255,255,255,0.08)',
              color: activeEco === 'dist' ? '#fff' : 'rgba(255,255,255,0.5)',
              boxShadow: activeEco === 'dist' ? `0 0 24px ${COLORS.secondary}50` : 'none',
            }}
          >
            <Warehouse style={{ width: 16, height: 16, display: 'inline', verticalAlign: -3, marginRight: 8 }} />
            {lang === 'en' ? 'For Distributors' : lang === 'ru' ? 'Для дистрибьюторов' : 'Барои дистрибюторҳо'}
          </button>
          <button
            onClick={() => setActiveEco('pharm')}
            data-testid="eco-tab-pharm"
            style={{
              padding: '12px 32px', fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 14,
              border: 'none', cursor: 'pointer', transition: 'all 0.35s ease',
              borderRadius: '0 50px 50px 0',
              background: activeEco === 'pharm' ? '#F59E0B' : 'rgba(255,255,255,0.08)',
              color: activeEco === 'pharm' ? '#0e1e2f' : 'rgba(255,255,255,0.5)',
              boxShadow: activeEco === 'pharm' ? `0 0 24px rgba(245,158,11,0.4)` : 'none',
            }}
          >
            <Pill style={{ width: 16, height: 16, display: 'inline', verticalAlign: -3, marginRight: 8 }} />
            {lang === 'en' ? 'For Pharmacies' : lang === 'ru' ? 'Для аптек' : 'Барои доруханаҳо'}
          </button>
        </div>

        {/* Ecosystem Visual */}
        <div className="eco-visual-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 200px 1fr', gap: 20, alignItems: 'center', minHeight: 440, position: 'relative' }}>

          {/* Left Card — D-Ops */}
          <div style={{
            borderRadius: 20, overflow: 'hidden', position: 'relative',
            border: `2px solid ${activeEco === 'dist' ? COLORS.secondary : 'rgba(255,255,255,0.08)'}`,
            boxShadow: activeEco === 'dist' ? `0 0 40px ${COLORS.secondary}30, 0 0 80px ${COLORS.secondary}10` : '0 4px 20px rgba(0,0,0,0.3)',
            transition: 'all 0.5s ease', transform: activeEco === 'dist' ? 'scale(1.02)' : 'scale(0.98)',
            opacity: activeEco === 'dist' ? 1 : 0.5,
          }} data-testid="eco-dops-card">
            <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
              <img src="https://images.pexels.com/photos/4483942/pexels-photo-4483942.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Warehouse worker scanning inventory"
                loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(14,30,47,0.3) 0%, rgba(14,30,47,0.9) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 12, left: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Warehouse style={{ width: 16, height: 16, color: COLORS.secondary }} />
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff' }}>D-Ops</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>
                  {lang === 'en' ? 'Distributor Operations' : lang === 'ru' ? 'Операции дистрибьютора' : 'Амалиёти дистрибютор'}
                </span>
              </div>
              <div style={{
                position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 5,
                background: `${COLORS.secondary}20`, border: `1px solid ${COLORS.secondary}40`,
                borderRadius: 8, padding: '4px 10px',
                opacity: activeEco === 'dist' ? 1 : 0, transition: 'opacity 0.4s',
              }}>
                <Activity style={{ width: 10, height: 10, color: COLORS.secondary }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.secondary, fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'en' ? 'TRACKING' : lang === 'ru' ? 'ОТСЛЕЖ.' : 'ПАЙГИРӢ'}
                </span>
              </div>
            </div>

            <div style={{ background: '#15253a' }}>
              <div style={{ display: 'flex', gap: 0, padding: '8px 16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {(lang === 'en' ? ['Stock', 'Orders', 'Analytics'] : lang === 'ru' ? ['Запасы', 'Заказы', 'Аналитика'] : ['Захира', 'Фармоиш', 'Таҳлил']).map((tab, i) => (
                  <div key={i} style={{
                    padding: '6px 14px', fontSize: 10, fontWeight: 700, fontFamily: 'Outfit, sans-serif',
                    color: i === 0 ? COLORS.secondary : 'rgba(255,255,255,0.3)',
                    borderBottom: i === 0 ? `2px solid ${COLORS.secondary}` : '2px solid transparent',
                  }}>{tab}</div>
                ))}
              </div>
              <div style={{ padding: '8px 16px 12px' }}>
                {[
                  { name: 'Insulin Glargine', qty: 1240, status: 'ok' },
                  { name: 'Amoxicillin 500mg', qty: 86, status: 'low' },
                  { name: 'Paracetamol', qty: 3500, status: 'ok' },
                  { name: 'Omeprazole 20mg', qty: 42, status: 'critical' },
                  { name: 'Metformin 850mg', qty: 890, status: 'ok' },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '1fr 50px 42px', gap: 6,
                    padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.03)',
                    alignItems: 'center',
                    opacity: activeEco === 'dist' ? 1 : 0.6,
                    transform: activeEco === 'dist' ? 'translateX(0)' : 'translateX(-10px)',
                    transition: `all 0.4s ease ${i * 0.08}s`,
                  }}>
                    <span style={{ fontSize: 10, color: '#fff', fontWeight: 500 }}>{row.name}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'right', fontFamily: 'monospace' }}>{row.qty.toLocaleString()}</span>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{
                        fontSize: 7, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                        background: row.status === 'ok' ? `${COLORS.secondary}20` : row.status === 'low' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
                        color: row.status === 'ok' ? COLORS.secondary : row.status === 'low' ? '#F59E0B' : '#EF4444',
                        textTransform: 'uppercase',
                      }}>
                        {row.status === 'ok' ? 'OK' : row.status === 'low' ? 'LOW' : 'CRIT'}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{
                  marginTop: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 8,
                  opacity: activeEco === 'dist' ? 1 : 0,
                  transform: activeEco === 'dist' ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'all 0.5s ease 0.3s',
                }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {lang === 'en' ? 'Weekly Demand' : lang === 'ru' ? 'Недельный спрос' : 'Талаботи ҳафтагӣ'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 28 }}>
                    {[45, 60, 40, 75, 55, 90, 70].map((h, i) => (
                      <div key={i} style={{ flex: 1, height: `${h}%`, background: i === 5 ? COLORS.secondary : `${COLORS.secondary}35`, borderRadius: 2 }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Center Ecosystem Ring */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{
              width: 130, height: 130, borderRadius: '50%', position: 'relative',
              border: `3px solid ${activeEco === 'dist' ? COLORS.secondary : '#F59E0B'}`,
              background: `radial-gradient(circle, ${activeEco === 'dist' ? COLORS.secondary + '15' : 'rgba(245,158,11,0.1)'} 0%, transparent 70%)`,
              boxShadow: `0 0 50px ${activeEco === 'dist' ? COLORS.secondary + '30' : 'rgba(245,158,11,0.25)'}`,
              transition: 'all 0.5s ease',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }} data-testid="eco-ring">
              <Shield style={{ width: 24, height: 24, color: activeEco === 'dist' ? COLORS.secondary : '#F59E0B', transition: 'color 0.5s' }} />
              <div style={{ fontSize: 10, fontWeight: 800, color: '#fff', marginTop: 4, textAlign: 'center', fontFamily: 'Outfit, sans-serif', lineHeight: 1.2 }}>
                Partnership<br />Sync
              </div>
            </div>

            {/* Distributor Metrics */}
            {[
              { icon: BarChart3, label: lang === 'en' ? 'Analytics' : lang === 'ru' ? 'Аналитика' : 'Таҳлил', top: '-10px', left: '-80px' },
              { icon: Package, label: lang === 'en' ? 'Inventory' : lang === 'ru' ? 'Инвентарь' : 'Захира', top: '50%', left: '-95px', mt: '-18px' },
              { icon: ClipboardList, label: lang === 'en' ? 'Catalog' : lang === 'ru' ? 'Каталог' : 'Каталог', bottom: '-10px', left: '-80px' },
            ].map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={`d-${i}`} style={{
                  position: 'absolute', top: m.top || 'auto', bottom: m.bottom || 'auto', left: m.left,
                  marginTop: m.mt || 0,
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: activeEco === 'dist' ? COLORS.secondary : 'rgba(255,255,255,0.06)',
                  borderRadius: 10, whiteSpace: 'nowrap',
                  opacity: activeEco === 'dist' ? 1 : 0,
                  transform: activeEco === 'dist' ? 'translateX(0) scale(1)' : 'translateX(20px) scale(0.8)',
                  transition: `all 0.45s ease ${i * 0.1}s`,
                  boxShadow: activeEco === 'dist' ? `0 4px 16px ${COLORS.secondary}40` : 'none',
                }} data-testid={`dist-metric-${i}`}>
                  <Icon style={{ width: 14, height: 14, color: '#fff' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: 'Outfit, sans-serif' }}>{m.label}</span>
                </div>
              );
            })}

            {/* Pharmacy Metrics */}
            {[
              { icon: Users, label: lang === 'en' ? 'CRM' : 'CRM', top: '-10px', right: '-55px' },
              { icon: Truck, label: lang === 'en' ? 'Deliveries' : lang === 'ru' ? 'Доставки' : 'Расонидан', top: '50%', right: '-80px', mt: '-18px' },
              { icon: Heart, label: lang === 'en' ? 'Client Relations' : lang === 'ru' ? 'Клиенты' : 'Муштариён', bottom: '-10px', right: '-95px' },
            ].map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={`p-${i}`} style={{
                  position: 'absolute', top: m.top || 'auto', bottom: m.bottom || 'auto', right: m.right,
                  marginTop: m.mt || 0,
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: activeEco === 'pharm' ? '#F59E0B' : 'rgba(255,255,255,0.06)',
                  borderRadius: 10, whiteSpace: 'nowrap',
                  opacity: activeEco === 'pharm' ? 1 : 0,
                  transform: activeEco === 'pharm' ? 'translateX(0) scale(1)' : 'translateX(-20px) scale(0.8)',
                  transition: `all 0.45s ease ${i * 0.1}s`,
                  boxShadow: activeEco === 'pharm' ? `0 4px 16px rgba(245,158,11,0.4)` : 'none',
                }} data-testid={`pharm-metric-${i}`}>
                  <Icon style={{ width: 14, height: 14, color: activeEco === 'pharm' ? '#0e1e2f' : '#fff' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: activeEco === 'pharm' ? '#0e1e2f' : '#fff', fontFamily: 'Outfit, sans-serif' }}>{m.label}</span>
                </div>
              );
            })}

            {/* Glow connector lines */}
            <div style={{
              position: 'absolute', top: '50%', left: -20, width: 20, height: 2,
              background: activeEco === 'dist' ? COLORS.secondary : 'rgba(255,255,255,0.1)',
              transition: 'background 0.5s', boxShadow: activeEco === 'dist' ? `0 0 8px ${COLORS.secondary}` : 'none',
            }} />
            <div style={{
              position: 'absolute', top: '50%', right: -20, width: 20, height: 2,
              background: activeEco === 'pharm' ? '#F59E0B' : 'rgba(255,255,255,0.1)',
              transition: 'background 0.5s', boxShadow: activeEco === 'pharm' ? `0 0 8px #F59E0B` : 'none',
            }} />
          </div>

          {/* Right Card — P-Care */}
          <div style={{
            borderRadius: 20, overflow: 'hidden', position: 'relative',
            border: `2px solid ${activeEco === 'pharm' ? '#F59E0B' : 'rgba(255,255,255,0.08)'}`,
            boxShadow: activeEco === 'pharm' ? `0 0 40px rgba(245,158,11,0.25), 0 0 80px rgba(245,158,11,0.08)` : '0 4px 20px rgba(0,0,0,0.3)',
            transition: 'all 0.5s ease', transform: activeEco === 'pharm' ? 'scale(1.02)' : 'scale(0.98)',
            opacity: activeEco === 'pharm' ? 1 : 0.5,
          }} data-testid="eco-pcare-card">
            <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
              <img src="https://images.unsplash.com/photo-1580281658223-9b93f18ae9ae?auto=format&fit=crop&w=800&q=80" alt="Pharmacist checking tablet"
                loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(14,30,47,0.3) 0%, rgba(14,30,47,0.9) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 12, left: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Pill style={{ width: 16, height: 16, color: '#F59E0B' }} />
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff' }}>P-Care</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>
                  {lang === 'en' ? 'Pharmacy Care' : lang === 'ru' ? 'Забота об аптеке' : 'Ғамхории доруханa'}
                </span>
              </div>
              <div style={{
                position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 5,
                background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: 8, padding: '4px 10px',
                opacity: activeEco === 'pharm' ? 1 : 0, transition: 'opacity 0.4s',
              }}>
                <Activity style={{ width: 10, height: 10, color: '#F59E0B' }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: '#F59E0B', fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'en' ? 'LIVE SYNC' : lang === 'ru' ? 'СИНХР.' : 'СИНХР.'}
                </span>
              </div>
            </div>

            <div style={{ background: '#15253a', padding: '0' }}>
              <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Warehouse style={{ width: 12, height: 12, color: COLORS.secondary }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.secondary, fontFamily: 'Outfit, sans-serif' }}>
                    {lang === 'en' ? 'Distributor Stock View' : lang === 'ru' ? 'Запасы дистрибьютора' : 'Захираи дистрибютор'}
                  </span>
                </div>
                <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>
                  {lang === 'en' ? 'via D-Ops' : lang === 'ru' ? 'через D-Ops' : 'тавассути D-Ops'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 0, padding: '8px 16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {(lang === 'en' ? ['Availability', 'Order', 'Pricing'] : lang === 'ru' ? ['Наличие', 'Заказ', 'Цены'] : ['Мавҷудият', 'Фармоиш', 'Нарх']).map((tab, i) => (
                  <div key={i} style={{
                    padding: '6px 14px', fontSize: 10, fontWeight: 700, fontFamily: 'Outfit, sans-serif',
                    color: i === 0 ? '#F59E0B' : 'rgba(255,255,255,0.3)',
                    borderBottom: i === 0 ? '2px solid #F59E0B' : '2px solid transparent',
                  }}>{tab}</div>
                ))}
              </div>

              <div style={{ padding: '8px 16px 12px' }}>
                {[
                  { name: 'Insulin Glargine', avail: true, qty: 1240, price: '$42.50' },
                  { name: 'Amoxicillin 500mg', avail: false, qty: 86, price: '$8.20' },
                  { name: 'Paracetamol', avail: true, qty: 3500, price: '$3.10' },
                  { name: 'Omeprazole 20mg', avail: false, qty: 42, price: '$12.80' },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '1fr 50px 50px 40px', gap: 6,
                    padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.03)',
                    alignItems: 'center',
                    opacity: activeEco === 'pharm' ? 1 : 0.6,
                    transform: activeEco === 'pharm' ? 'translateX(0)' : 'translateX(10px)',
                    transition: `all 0.4s ease ${i * 0.08}s`,
                  }}>
                    <span style={{ fontSize: 10, color: '#fff', fontWeight: 500 }}>{row.name}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'right', fontFamily: 'monospace' }}>{row.qty.toLocaleString()}</span>
                    <span style={{ fontSize: 10, color: '#F59E0B', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{row.price}</span>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: 6,
                        background: row.avail ? `${COLORS.secondary}25` : 'rgba(239,68,68,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {row.avail
                          ? <Check style={{ width: 10, height: 10, color: COLORS.secondary }} />
                          : <X style={{ width: 10, height: 10, color: '#EF4444' }} />}
                      </div>
                    </div>
                  </div>
                ))}

                <div style={{
                  marginTop: 10, display: 'flex', gap: 8, alignItems: 'center',
                  opacity: activeEco === 'pharm' ? 1 : 0,
                  transform: activeEco === 'pharm' ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'all 0.5s ease 0.3s',
                }}>
                  <div style={{
                    flex: 1, padding: '8px 12px', background: '#F59E0B', borderRadius: 8,
                    textAlign: 'center', fontSize: 10, fontWeight: 800, color: '#0e1e2f',
                    fontFamily: 'Outfit, sans-serif', cursor: 'pointer',
                  }}>
                    {lang === 'en' ? 'Quick Order from D-Ops' : lang === 'ru' ? 'Быстрый заказ из D-Ops' : 'Фармоиши зуд аз D-Ops'}
                  </div>
                  <div style={{
                    padding: '8px 12px', background: 'rgba(255,255,255,0.06)', borderRadius: 8,
                    fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'Outfit, sans-serif',
                  }}>
                    {lang === 'en' ? '4 items' : lang === 'ru' ? '4 поз.' : '4 ашё'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefit Strip */}
        <div className="eco-benefit-strip" style={{
          display: 'grid', gridTemplateColumns: '1fr 200px 1fr', gap: 20,
          padding: '32px 0 24px', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 40,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${COLORS.secondary}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TrendingUp style={{ width: 18, height: 18, color: COLORS.secondary }} />
            </div>
            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 13, color: '#fff', lineHeight: 1.3 }}>
                {lang === 'en' ? 'Boost Revenue & Stay Stocked' : lang === 'ru' ? 'Увеличивайте доход и запасы' : 'Даромадро зиёд кунед'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3, lineHeight: 1.4 }}>
                {lang === 'en' ? 'Reduce stockouts by 92% with predictive ordering.' : lang === 'ru' ? 'Сокращение дефицита на 92%.' : '92% кам кардани камбуд.'}
              </div>
            </div>
          </div>
          <div />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CreditCard style={{ width: 18, height: 18, color: '#F59E0B' }} />
            </div>
            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 13, color: '#fff', lineHeight: 1.3 }}>
                {lang === 'en' ? 'Eliminate Price Guessing & Save' : lang === 'ru' ? 'Забудьте о гадании по ценам' : 'Тахмини нархро бартараф кунед'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3, lineHeight: 1.4 }}>
                {lang === 'en' ? 'Automated pricing & unified workflows.' : lang === 'ru' ? 'Автоматическое ценообразование.' : 'Нархгузории автоматикӣ.'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crush Your Numbers CTA */}
      <div style={{ background: 'linear-gradient(135deg, #132a3e 0%, #0e1e2f 100%)', padding: '44px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="crush-numbers">
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(22px, 3vw, 34px)', fontWeight: 800, color: '#fff', margin: '0 0 8px', letterSpacing: '0.04em' }}>
            {lang === 'en' ? 'CRUSH YOUR NUMBERS' : lang === 'ru' ? 'ПОБЕЖДАЙТЕ В ЦИФРАХ' : 'РАҚАМҲОРО ФАТҲ КУНЕД'}
          </h3>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', margin: '0 0 28px' }}>
            {lang === 'en' ? 'One unified platform. Real-time data. Smarter decisions.' : lang === 'ru' ? 'Одна платформа. Данные в реальном времени. Умные решения.' : 'Як платформаи ягона. Маълумоти вақти воқеӣ. Қарорҳои зирак.'}
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/login')} data-testid="cta-dist-btn"
              style={{
                background: 'transparent', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 50,
                padding: '12px 28px', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
                fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
            >
              {lang === 'en' ? 'Learn more (Distributors)' : lang === 'ru' ? 'Подробнее (Дистрибьюторы)' : 'Бештар (Дистрибюторҳо)'} <ArrowRight style={{ width: 14, height: 14 }} />
            </button>
            <button onClick={() => navigate('/login')} data-testid="cta-pharm-btn"
              style={{
                background: COLORS.secondary, border: `2px solid ${COLORS.secondary}`, borderRadius: 50,
                padding: '12px 28px', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
                fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.secondaryHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = COLORS.secondary; }}
            >
              {lang === 'en' ? 'Learn more (Pharmacies)' : lang === 'ru' ? 'Подробнее (Аптеки)' : 'Бештар (Доруханаҳо)'} <ArrowRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
